import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  Download,
  ExternalLink,
  Link2,
  Lightbulb,
  Menu,
  MessageSquareWarning,
  MessagesSquare,
  PackageSearch,
  RefreshCw,
  Search,
  Star,
  X,
} from "lucide-react";
import { moduleConfigs } from "../operations/moduleConfigs";
import { moduleDataSource } from "../operations/moduleDataSource";
import type { ModuleRow } from "../operations/types";

const config = moduleConfigs.reviews!;
type RankingMode = "rate" | "count";
type ReviewConversationMessage = {
  id: string;
  sender: "customer" | "agent" | "system";
  senderName: string;
  time: string;
  content: string;
  imageUrl?: string;
};

function asText(value: unknown) {
  return value === undefined || value === null ? "" : String(value);
}

function asNumber(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function platformOf(row: ModuleRow) {
  return asText(row.platform) || asText(row.store).split("/").at(-1)?.trim() || "其他";
}

function productKey(row: ModuleRow) {
  const productId = asText(row.productId);
  return asText(row.productLink) || (productId ? `${platformOf(row)}::${productId}` : `unlinked-${row.order}`);
}

function masterProductKey(row: ModuleRow) {
  return asText(row.masterProductId)
    || asText(row.productName).trim().toLocaleLowerCase().replace(/\s+/g, "-")
    || productKey(row);
}

function issueSummary(productRows: ModuleRow[]) {
  const issues = productRows.map((row) => asText(row.issue));
  return [...new Set(issues)]
    .map((name) => ({ name, count: issues.filter((item) => item === name).length }))
    .sort((left, right) => right.count - left.count)[0]?.name || "待分析";
}

function conversationMessages(row: ModuleRow) {
  const raw = row.conversationMessages || row.chatMessages;
  if (!raw) return [] as ReviewConversationMessage[];
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((item, index) => {
    if (!item || typeof item !== "object") return [];
    const message = item as Record<string, unknown>;
    const sender = message.sender === "customer" || message.sender === "agent" || message.sender === "system"
      ? message.sender
      : "system";
    const content = asText(message.content).trim();
    const imageUrl = asText(message.imageUrl).trim();
    if (!content && !imageUrl) return [];
    return [{
      id: asText(message.id) || `message-${index}`,
      sender,
      senderName: asText(message.senderName) || (sender === "customer" ? "客户" : sender === "agent" ? "客服" : "系统"),
      time: asText(message.time),
      content,
      ...(imageUrl ? { imageUrl } : {}),
    } satisfies ReviewConversationMessage];
  });
}

const optimizationSuggestions: Record<string, string> = {
  "物流时效": "在商品页和客服首响中明确发货与预计送达时间，对临近超时订单主动提醒并升级物流跟进。",
  "包装破损": "升级外包装缓冲和封装检查，出库前增加包装抽检，并为补发处理设置明确时限。",
  "包装漏液": "优化瓶口密封与运输防护，增加倒置、挤压测试，对同批次商品安排包装复检。",
  "商品描述": "核对详情页卖点、规格与真实使用表现，删除容易造成误解的表述并补充适用条件。",
  "客服态度": "针对高频咨询补充标准答复与升级路径，对相关客服安排案例复盘和服务话术训练。",
  "肤感体验": "在详情页补充适用肤质、用量和使用方法，降低使用方式或效果预期偏差带来的差评。",
  "商品效果": "明确效果周期和适用人群，避免绝对化承诺，并补充客服的效果预期说明话术。",
  "清洁体验": "补充清洁力、适用肤质和使用频次说明，客服推荐前先确认客户肤质与使用场景。",
};

function optimizationSuggestion(issue: string) {
  return optimizationSuggestions[issue] ?? "复核该类差评的商品、履约和客服记录，归纳高频原因，明确责任人与完成时间后持续观察差评率变化。";
}

function enrichRows(rows: ModuleRow[]) {
  const seeds = new Map([...(config.seed ?? []), ...(config.syncRows ?? [])].map((row) => [row.id, row]));
  const merged = rows.map((row) => {
    const seed = seeds.get(row.id);
    if (!seed) return row;
    return { ...row, ...seed, status: row.status ?? seed.status ?? "待处理", overdue: row.overdue ?? seed.overdue ?? false };
  });
  const existingIds = new Set(merged.map((row) => row.id));
  return [...merged, ...config.seed.filter((row) => !existingIds.has(row.id)).map((row) => ({ ...row }))];
}

function exportReviews(rows: ModuleRow[]) {
  const columns = [
    ["order", "订单编号"], ["productName", "商品名称"], ["productLink", "商品链接"], ["store", "店铺 / 平台"],
    ["agent", "客服名称"], ["time", "评价时间"], ["rating", "星级"], ["issue", "问题类型"], ["content", "评价内容"], ["status", "处理状态"],
  ] as const;
  const csv = `\ufeff${[columns.map((column) => column[1]), ...rows.map((row) => columns.map((column) => asText(row[column[0]])))]
    .map((line) => line.map((cell) => `"${cell.replaceAll('"', '""')}"`).join(",")).join("\r\n")}`;
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = Object.assign(document.createElement("a"), { href: url, download: "近30天订单差评-商品分析.csv" });
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ReviewsPage({ onMenuOpen }: { onMenuOpen: () => void }) {
  const [rows, setRows] = useState<ModuleRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("");
  const [issue, setIssue] = useState("");
  const [status, setStatus] = useState("");
  const [rankingMode, setRankingMode] = useState<RankingMode>("rate");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedListing, setSelectedListing] = useState("");
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(() => new Set());
  const [selectedReview, setSelectedReview] = useState<ModuleRow | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void moduleDataSource.load(config).then((data) => {
      if (!active) return;
      setRows(enrichRows(data));
      setLoaded(true);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (loaded) void moduleDataSource.save(config, rows);
  }, [loaded, rows]);

  const listings = useMemo(() => {
    const groups = new Map<string, ModuleRow[]>();
    rows.forEach((row) => groups.set(productKey(row), [...(groups.get(productKey(row)) ?? []), row]));
    return [...groups.entries()].map(([key, productRows]) => {
      const first = productRows[0]!;
      const totalOrders = Math.max(...productRows.map((row) => asNumber(row.productOrders30d)), productRows.length);
      const mainIssue = issueSummary(productRows);
      return {
        key,
        masterKey: masterProductKey(first),
        name: asText(first.productName) || "待关联商品",
        link: asText(first.productLink),
        productId: asText(first.productId) || "--",
        store: asText(first.store),
        platform: platformOf(first),
        negativeCount: productRows.length,
        totalOrders,
        negativeRate: productRows.length / totalOrders * 100,
        unresolvedCount: productRows.filter((row) => row.status !== "已完成").length,
        oneStarCount: productRows.filter((row) => asText(row.rating).startsWith("1")).length,
        mainIssue,
        suggestion: optimizationSuggestion(mainIssue),
      };
    });
  }, [rows]);

  const products = useMemo(() => {
    const groups = new Map<string, ModuleRow[]>();
    rows.forEach((row) => groups.set(masterProductKey(row), [...(groups.get(masterProductKey(row)) ?? []), row]));
    return [...groups.entries()].map(([key, productRows]) => {
      const first = productRows[0]!;
      const productListings = listings
        .filter((listing) => listing.masterKey === key)
        .sort((left, right) => right.negativeRate - left.negativeRate || right.negativeCount - left.negativeCount);
      const totalOrders = productListings.reduce((sum, listing) => sum + listing.totalOrders, 0);
      const mainIssue = issueSummary(productRows);
      return {
        key,
        name: asText(first.productName) || "待关联商品",
        masterProductId: asText(first.masterProductId) || "名称匹配",
        listings: productListings,
        platforms: [...new Set(productListings.map((listing) => listing.platform))],
        negativeCount: productRows.length,
        totalOrders,
        negativeRate: totalOrders ? productRows.length / totalOrders * 100 : 0,
        unresolvedCount: productRows.filter((row) => row.status !== "已完成").length,
        oneStarCount: productRows.filter((row) => asText(row.rating).startsWith("1")).length,
        mainIssue,
        suggestion: optimizationSuggestion(mainIssue),
      };
    });
  }, [listings, rows]);

  const rankedProducts = useMemo(() => [...products].sort((left, right) => rankingMode === "rate"
    ? right.negativeRate - left.negativeRate || right.negativeCount - left.negativeCount
    : right.negativeCount - left.negativeCount || right.negativeRate - left.negativeRate), [products, rankingMode]);

  useEffect(() => {
    const multiPlatformProducts = products.filter((product) => product.listings.length > 1).map((product) => product.key);
    if (multiPlatformProducts.length) {
      setExpandedProducts((current) => current.size ? current : new Set(multiPlatformProducts));
    }
  }, [products]);

  const visibleRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch = !keyword || [row.order, row.productName, row.productLink, row.store, row.agent, row.content]
        .some((value) => asText(value).toLowerCase().includes(keyword));
      return matchesSearch
        && (!platform || platformOf(row) === platform)
        && (!issue || row.issue === issue)
        && (!status || row.status === status)
        && (!selectedProduct || masterProductKey(row) === selectedProduct)
        && (!selectedListing || productKey(row) === selectedListing);
    });
  }, [issue, platform, rows, search, selectedListing, selectedProduct, status]);

  const issueStats = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((row) => counts.set(asText(row.issue), (counts.get(asText(row.issue)) ?? 0) + 1));
    return [...counts.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [rows]);

  const totalOrders = products.reduce((sum, product) => sum + product.totalOrders, 0);
  const unresolved = rows.filter((row) => row.status !== "已完成").length;
  const overdue = rows.filter((row) => row.overdue === true && row.status !== "已完成").length;
  const overallRate = totalOrders ? rows.length / totalOrders * 100 : 0;
  const highestRisk = [...products].sort((a, b) => b.negativeRate - a.negativeRate)[0];
  const maxRankValue = Math.max(...rankedProducts.map((product) => rankingMode === "rate" ? product.negativeRate : product.negativeCount), 1);
  const platforms = [...new Set(rows.map(platformOf))];
  const issues = [...new Set(rows.map((row) => asText(row.issue)).filter(Boolean))];
  const statuses = [...new Set(rows.map((row) => asText(row.status)).filter(Boolean))];
  const selectedLabel = selectedListing
    ? (() => {
      const listing = listings.find((item) => item.key === selectedListing);
      return listing ? `${listing.name} · ${listing.platform}` : "平台商品";
    })()
    : products.find((item) => item.key === selectedProduct)?.name;
  const selectedConversation = selectedReview ? conversationMessages(selectedReview) : [];

  const syncReviews = async () => {
    setSyncing(true);
    const synced = enrichRows(await moduleDataSource.sync(config, rows));
    setRows(synced);
    setNotice(synced.length > rows.length ? `同步完成，新增 ${synced.length - rows.length} 条差评` : "同步完成，没有重复数据");
    setSyncing(false);
    window.setTimeout(() => setNotice(null), 2400);
  };

  const advanceReview = () => {
    if (!selectedReview) return;
    const current = asText(selectedReview.status);
    const next = current === "待处理" ? "处理中" : "已完成";
    const updated = { ...selectedReview, status: next };
    setRows((currentRows) => currentRows.map((row) => row.id === selectedReview.id ? updated : row));
    setSelectedReview(updated);
    setNotice(`订单 ${selectedReview.order} 已更新为“${next}”`);
    window.setTimeout(() => setNotice(null), 2400);
  };

  const toggleProduct = (key: string) => {
    setExpandedProducts((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setSelectedProduct((current) => current === key && !selectedListing ? "" : key);
    setSelectedListing("");
  };

  const selectListing = (masterKey: string, listingKey: string) => {
    setSelectedProduct(masterKey);
    setSelectedListing((current) => current === listingKey ? "" : listingKey);
  };

  return (
    <main className="main reviewsMain">
      <header className="topbar"><button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button><strong>商品差评分析</strong><span>近30天商品链接与差评处理</span></header>
      <div className="reviewsContent">
        <section className="reviewsHead">
          <div><h1>商品差评分析</h1><p>同款商品汇总全平台表现，展开查看各平台差评差异与优化方向</p></div>
          <div className="reviewsActions"><button className="button" disabled={!visibleRows.length} onClick={() => exportReviews(visibleRows)}><Download />导出分析</button><button className="button primary" disabled={syncing} onClick={() => void syncReviews()}><RefreshCw className={syncing ? "spin" : ""} />{syncing ? "同步中" : "同步差评"}</button></div>
        </section>

        <section className="reviewsSourceNotice"><Link2 /><div><strong>同款商品跨平台汇总，平台链接独立分析</strong><span>优先使用同款商品编码归并，未设置编码时按完整商品名称匹配；不同规格不会合并。</span></div></section>

        <section className="reviewsSummaryBand">
          <article><span>近30天差评</span><strong>{rows.length}</strong><small>{products.length} 款同款商品 · {listings.length} 个平台链接</small><MessageSquareWarning /></article>
          <article><span>整体差评率</span><strong>{overallRate.toFixed(2)}%</strong><small>{totalOrders.toLocaleString("zh-CN")} 笔商品订单</small><BarChart3 /></article>
          <article className="danger"><span>最高风险商品</span><strong>{highestRisk?.negativeRate.toFixed(2) ?? "0.00"}%</strong><small>{highestRisk?.name ?? "暂无商品"}</small><AlertTriangle /></article>
          <article><span>待处理 / 超时</span><strong>{unresolved} / {overdue}</strong><small>优先完成问题闭环</small><Clock3 /></article>
        </section>

        <section className="reviewsInsights">
          <div className="productRankingPanel">
            <header><div><h2>同款商品全平台排名</h2><p>主行汇总全平台，展开后查看平台和店铺差异</p></div><div className="reviewsMode"><button className={rankingMode === "rate" ? "active" : ""} onClick={() => setRankingMode("rate")}>按差评率</button><button className={rankingMode === "count" ? "active" : ""} onClick={() => setRankingMode("count")}>按差评量</button></div></header>
            <div className="productRankingList">{rankedProducts.map((product, index) => {
              const value = rankingMode === "rate" ? product.negativeRate : product.negativeCount;
              const expanded = expandedProducts.has(product.key);
              return <article className="productRankingGroup" key={product.key}>
                <button className={`productRankingMaster ${selectedProduct === product.key && !selectedListing ? "active" : ""}`} aria-expanded={expanded} onClick={() => toggleProduct(product.key)}>
                  <b className={index < 3 ? "top" : ""}>{index + 1}</b>
                  <span className="productRankingName"><strong>{product.name}</strong><small>{product.platforms.length}个平台 · {product.platforms.join(" / ")} · 同款编码 {product.masterProductId}</small></span>
                  <span className="productRankingBar"><i><b style={{ width: `${value / maxRankValue * 100}%` }} /></i><small>全平台主要问题：{product.mainIssue}</small></span>
                  <span className="productRankingValue"><strong>{rankingMode === "rate" ? `${product.negativeRate.toFixed(2)}%` : product.negativeCount}</strong><small>{product.negativeCount}条差评 / {product.totalOrders}单</small></span>
                  <ChevronDown className={expanded ? "expanded" : ""} />
                </button>
                {expanded && <div className="productPlatformBreakdown">{product.listings.map((listing) => <button key={listing.key} className={selectedListing === listing.key ? "active" : ""} onClick={() => selectListing(product.key, listing.key)}>
                  <b>{listing.platform}</b>
                  <span><strong>{listing.store.split("/")[0]?.trim()}</strong><small title={listing.link}>{listing.link || listing.productId}</small></span>
                  <span><strong>{listing.negativeRate.toFixed(2)}%</strong><small>{listing.negativeCount}条差评 / {listing.totalOrders}单</small></span>
                  <span><strong>{listing.mainIssue}</strong><small>{listing.unresolvedCount}条待处理</small></span>
                  <ChevronRight />
                </button>)}</div>}
              </article>;
            })}</div>
          </div>

          <aside className="reviewIssuePanel">
            <header><div><h2>差评原因分布</h2><p>用于判断优先优化方向</p></div><PackageSearch /></header>
            <div>{issueStats.map((item) => <article key={item.name}><span><strong>{item.name}</strong><b>{item.count} 条</b></span><i><b style={{ width: `${rows.length ? item.count / rows.length * 100 : 0}%` }} /></i><small>{rows.length ? (item.count / rows.length * 100).toFixed(1) : 0}%</small></article>)}</div>
          </aside>
        </section>

        <section className="reviewRecommendations">
          <header><div><h2>同款商品优化建议</h2><p>根据全平台主要差评问题生成方向，平台差异可在上方展开查看</p></div><Lightbulb /></header>
          <div>{rankedProducts.map((product, index) => <button key={product.key} className={selectedProduct === product.key && !selectedListing ? "active" : ""} onClick={() => { setSelectedProduct((current) => current === product.key ? "" : product.key); setSelectedListing(""); }}>
            <b>{index + 1}</b>
            <span><strong>{product.name}</strong><small>{product.platforms.join(" / ")} · {product.mainIssue} · 全平台差评率 {product.negativeRate.toFixed(2)}%</small></span>
            <p>{product.suggestion}</p>
            <em>{index === 0 ? "优先优化" : "持续关注"}</em>
            <ChevronRight />
          </button>)}</div>
        </section>

        <section className="reviewsTablePanel">
          <header><div><h2>差评订单明细</h2><p>{selectedProduct ? `已筛选：${selectedLabel}` : `共 ${visibleRows.length} 条记录`}</p></div>{selectedProduct && <button className="reviewsClearProduct" onClick={() => { setSelectedProduct(""); setSelectedListing(""); }}>清除商品筛选 <X /></button>}<div className="reviewsFilters"><label><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索商品链接、订单、店铺或评价" /></label><select value={platform} onChange={(event) => setPlatform(event.target.value)}><option value="">全部平台</option>{platforms.map((item) => <option key={item}>{item}</option>)}</select><select value={issue} onChange={(event) => setIssue(event.target.value)}><option value="">全部问题</option>{issues.map((item) => <option key={item}>{item}</option>)}</select><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">全部状态</option>{statuses.map((item) => <option key={item}>{item}</option>)}</select></div></header>
          <div className="reviewsTableScroll"><table><thead><tr><th>商品链接</th><th>订单 / 客服</th><th>店铺 / 平台</th><th>星级</th><th>问题</th><th>评价内容</th><th>评价时间</th><th>处理状态</th></tr></thead><tbody>{visibleRows.map((row) => <tr key={row.id} onClick={() => setSelectedReview(row)}><td><strong>{asText(row.productName) || "待关联商品"}</strong><small title={asText(row.productLink)}><Link2 />{asText(row.productLink) || asText(row.productId) || "链接待采集"}</small></td><td><strong>{asText(row.order)}</strong><small>{asText(row.agent)}</small></td><td><strong>{platformOf(row)}</strong><small>{asText(row.store).split("/")[0]?.trim()}</small></td><td><span className={`reviewRating ${asText(row.rating).startsWith("1") ? "critical" : ""}`}><Star />{asText(row.rating)}</span></td><td><span className="reviewIssueTag">{asText(row.issue)}</span></td><td className="reviewContentCell">{asText(row.content)}</td><td>{asText(row.time)}</td><td><span className={`reviewStatus ${row.status === "已完成" ? "done" : row.status === "处理中" ? "working" : "pending"}`}>{asText(row.status)}</span></td></tr>)}</tbody></table>{!visibleRows.length && <div className="operationsEmpty">当前条件下没有差评记录</div>}</div>
          <footer><span>共 {visibleRows.length} 条差评订单</span><span>点击记录查看评价证据与处理信息</span></footer>
        </section>
      </div>

      {selectedReview && <div className="modalLayer" onMouseDown={(event) => event.target === event.currentTarget && setSelectedReview(null)}><section className={`reviewDetailModal ${selectedConversation.length ? "hasConversation" : ""}`}>
        <header><div><strong>差评处理详情</strong><span>{asText(selectedReview.order)} · {asText(selectedReview.productName)}</span></div><button className="iconButton" aria-label="关闭" onClick={() => setSelectedReview(null)}><X /></button></header>
        <div className="reviewDetailBody"><section><span>客户评价</span><blockquote>{asText(selectedReview.content)}</blockquote><div className="reviewDetailMeta"><div><span>问题类型</span><strong>{asText(selectedReview.issue)}</strong></div><div><span>评价星级</span><strong>{asText(selectedReview.rating)}</strong></div><div><span>评价时间</span><strong>{asText(selectedReview.time)}</strong></div><div><span>负责客服</span><strong>{asText(selectedReview.agent)}</strong></div></div><div className="reviewSuggestionBox"><span><Lightbulb />优化建议</span><p>{optimizationSuggestion(asText(selectedReview.issue))}</p></div>{selectedConversation.length > 0 && <div className="reviewConversation"><header><span><MessagesSquare />客户与客服沟通记录</span><small>{selectedConversation.length} 条消息</small></header><div>{selectedConversation.map((message) => <article className={message.sender} key={message.id}><div><strong>{message.senderName}</strong>{message.time && <time>{message.time}</time>}</div>{message.content && <p>{message.content}</p>}{message.imageUrl && <a href={message.imageUrl} target="_blank" rel="noreferrer"><img src={message.imageUrl} alt="聊天记录图片" /></a>}</article>)}</div></div>}</section><aside><span>关联商品</span><strong>{asText(selectedReview.productName)}</strong><small>{asText(selectedReview.productId)}</small><p>{asText(selectedReview.store)}</p>{asText(selectedReview.productLink) && <a href={asText(selectedReview.productLink)} target="_blank" rel="noreferrer"><ExternalLink />打开商品链接</a>}<div><span>当前状态</span><b className={`reviewStatus ${selectedReview.status === "已完成" ? "done" : selectedReview.status === "处理中" ? "working" : "pending"}`}>{asText(selectedReview.status)}</b></div></aside></div>
        <footer><button className="button" onClick={() => setSelectedReview(null)}>关闭</button>{selectedReview.status !== "已完成" && <button className="button primary" onClick={advanceReview}><CheckCircle2 />{selectedReview.status === "待处理" ? "开始处理" : "完成处理"}</button>}</footer>
      </section></div>}
      {notice && <button className="toast" onClick={() => setNotice(null)}>{notice}</button>}
    </main>
  );
}
